import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, LogOut, Search, Package, User, Building2, CheckSquare, Settings, AlertTriangle, Archive, Hammer, Palette, Boxes, Shield, Calendar, Ship, PoundSterling, Eye, Edit, Check, Truck, RefreshCw, CreditCard, Send, Loader2, Trash2, Heart } from 'lucide-react';
import StatusChart from '../components/StatusChart';
import StatusBadge from '../components/StatusBadge';
import DemoTour from '../components/DemoTour';
import { DesignerQueueBadge } from '../components/DesignerServicePanel';

// Helper function to safely convert to number
const safeNumber = (value, defaultValue = 0) => {
  const num = parseFloat(value);
  return isNaN(num) ? defaultValue : num;
};

function Dashboard() {
  const navigate = useNavigate();
  const { user, token, logout, API, companyContext } = useContext(AuthContext);
  
  // Demo tour state
  const isDemo = user?.role === 'demo' || user?.is_demo;
  const [showTour, setShowTour] = useState(false);
  
  // Check if we should show the tour for demo users
  useEffect(() => {
    if (isDemo) {
      const tourCompleted = localStorage.getItem('demo_tour_completed');
      if (!tourCompleted) {
        // Small delay to let the page load first
        const timer = setTimeout(() => setShowTour(true), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [isDemo]);
  
  // CRITICAL: Redirect trade users immediately
  useEffect(() => {
    if (user && user.role === 'trade') {
      navigate('/trade-dashboard', { replace: true });
    }
  }, [user, navigate]);
  
  // Redirect worktops-only companies to worktops dashboard
  useEffect(() => {
    if (companyContext?.features?.memorials_enabled === false && companyContext?.features?.worktops_enabled === true) {
      navigate('/worktops', { replace: true });
    }
  }, [companyContext, navigate]);
  
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoadFailed, setOrdersLoadFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedOrderType, setSelectedOrderType] = useState(null);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [worktopsCount, setWorktopsCount] = useState(0);
  // Customer Agreements (traffic light) — fetched once per dashboard load
  const [agreementSummary, setAgreementSummary] = useState({ enabled: false, statuses: {} });
  const [urgentTasks, setUrgentTasks] = useState([]);
  const [showUrgentPopup, setShowUrgentPopup] = useState(false);
  const [pendingArtworkPopup, setPendingArtworkPopup] = useState(false);

  // Snooze all reminder/urgent popups until the end of today (per user, per device)
  const SNOOZE_KEY = `md_snooze_reminders_${user?.id || 'anon'}`;
  const remindersSnoozed = () => {
    try { return localStorage.getItem(SNOOZE_KEY) === new Date().toDateString(); } catch { return false; }
  };
  const snoozeRemindersForToday = () => {
    try { localStorage.setItem(SNOOZE_KEY, new Date().toDateString()); } catch {}
    setShowUrgentPopup(false);
    setShowArtworkReminderPopup(false);
    setShowFittingPopup(false);
    setShowMaintenancePopup(false);
    toast.success('Reminders and urgent task alerts snoozed for the rest of today');
  };
  const [artworkReminders, setArtworkReminders] = useState([]);
  const [showArtworkReminderPopup, setShowArtworkReminderPopup] = useState(false);
  const [fittingReminders, setFittingReminders] = useState({ ready_to_fit: [], upcoming_reminders: [], total_alerts: 0 });
  const [showFittingPopup, setShowFittingPopup] = useState(false);
  const [dueMaintenance, setDueMaintenance] = useState([]);
  const [showMaintenancePopup, setShowMaintenancePopup] = useState(false);
  const [users, setUsers] = useState([]);
  const [taskDialog, setTaskDialog] = useState(false);
  // Super admin: company filter for viewing orders
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('set-in-stone'); // Default to Set in Stone
  const [companiesList, setCompaniesList] = useState([]);
  const [taskForm, setTaskForm] = useState({
    order_id: '',
    title: '',
    description: '',
    priority: 'normal',
    assigned_to: '',
    timer_duration_hours: '',
    due_date: ''
  });
  
  // Editing locks state - shows which orders are being edited by other users
  const [editingLocks, setEditingLocks] = useState([]);
  const isAdmin = ['admin', 'super_admin', 'company_admin', 'manager', 'demo'].includes(user?.role);
  
  // China Ordering state (for Set In Stone admins only)
  const isSISAdmin = (user?.role === 'admin' || user?.role === 'super_admin') && user?.company_id === 'set-in-stone';
  const [chinaOrders, setChinaOrders] = useState([]);
  const [pendingChinaOrdersCount, setPendingChinaOrdersCount] = useState(0);
  const [showChinaQuoteDialog, setShowChinaQuoteDialog] = useState(false);
  const [selectedChinaOrder, setSelectedChinaOrder] = useState(null);
  const [chinaQuoteForm, setChinaQuoteForm] = useState({
    china_cost: '',
    markup_amount: '',
    shipping_cost: '',
    quote_notes: ''
  });
  const [submittingChinaQuote, setSubmittingChinaQuote] = useState(false);

  // Overdue subscriptions state (super admin only)
  const [overdueSubscriptions, setOverdueSubscriptions] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);

  // Family pages — orders awaiting a reply to a family message
  const [awaitingReply, setAwaitingReply] = useState([]);
  const fetchAwaitingReply = async () => {
    if (!isAdmin) return;
    try {
      const r = await axios.get(`${API}/orders/family/awaiting-reply`, { headers: { Authorization: `Bearer ${token}` } });
      setAwaitingReply(r.data?.orders || []);
    } catch (e) { /* ignore */ }
  };
  const workingDaysSince = (iso) => {
    if (!iso) return 0;
    let d = new Date(iso), n = 0; const today = new Date();
    while (d < today) { d.setDate(d.getDate() + 1); const wd = d.getDay(); if (wd !== 0 && wd !== 6) n++; }
    return n;
  };

  // Fetch active editing locks
  const fetchEditingLocks = async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API}/orders/editing-locks/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingLocks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch editing locks');
    }
  };

  // China Ordering functions (for Set In Stone admins)
  const fetchChinaOrders = async () => {
    try {
      const response = await axios.get(`${API}/group-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const all = response.data || [];
      setChinaOrders(all);
      const pending = all.filter(o => o.status === 'submitted').length;
      setPendingChinaOrdersCount(pending);

      // ---- Client-side popup notification when a quote arrives ----
      // Any group order belonging to the logged-in user's company that has just
      // moved to status='quoted' since the last time they checked should pop a
      // toast. We track acknowledged IDs in localStorage so the popup only
      // appears once per quote per device.
      const storageKey = `gob_seen_quotes_${user?.id || user?.email || 'anon'}`;
      let seen = [];
      try { seen = JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch (_) { seen = []; }
      const myCompanyId = user?.company_id;
      const fresh = all.filter(o =>
        o.status === 'quoted' &&
        o.company_id === myCompanyId &&
        !seen.includes(o.id)
      );
      if (fresh.length > 0) {
        fresh.forEach(o => {
          const amount = o.quote_amount != null ? `£${Number(o.quote_amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '';
          toast.success(
            `Quote received${amount ? `: ${amount}` : ''}`,
            {
              description: `${o.reference_number || o.job_name || 'Wholesale order'} is ready to review.`,
              duration: 10000,
              action: {
                label: 'View',
                onClick: () => navigate('/group-ordering')
              }
            }
          );
        });
        const nextSeen = Array.from(new Set([...seen, ...fresh.map(o => o.id)]));
        try { localStorage.setItem(storageKey, JSON.stringify(nextSeen)); } catch (_) { /* ignore quota errors */ }
      }
    } catch (error) {
      console.error('Failed to fetch trade wholesale orders:', error);
    }
  };

  // Overdue subscriptions functions (super admin only)
  const fetchOverdueSubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/overdue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverdueSubscriptions(response.data.overdue_companies || []);
    } catch (error) {
      console.error('Failed to fetch overdue subscriptions:', error);
    }
  };

  const sendPaymentReminder = async (companyId) => {
    setSendingReminder(companyId);
    try {
      const response = await axios.post(`${API}/subscriptions/send-payment-reminder`, {
        company_id: companyId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.sent_to?.length > 0) {
        toast.success(`Payment reminder sent to ${response.data.sent_to.join(', ')}`);
      } else {
        toast.error('Failed to send reminder — no valid email addresses');
      }
      fetchOverdueSubscriptions();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send reminder');
      toast.error(msg);
    } finally {
      setSendingReminder(null);
    }
  };

  const dismissOverdueReminder = async (companyId, companyName) => {
    // Removes the company from the overdue banner without touching billing dates.
    // Auto-reappears later if subscription_ends_at moves into the past again.
    if (!window.confirm(`Hide the overdue reminder for ${companyName}?\n\nThis won't change their billing date — use it when they've already paid via Stripe and you just want the banner gone.`)) return;
    try {
      await axios.post(`${API}/subscriptions/overdue/${companyId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reminder dismissed');
      fetchOverdueSubscriptions();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to dismiss reminder');
    }
  };

  const syncFromStripe = async (companyId, companyName) => {
    // Pulls the real current_period_end from Stripe and updates our copy,
    // preserving the actual billing cycle. Use when a client's sub is live
    // on Stripe but our DB still thinks they're overdue.
    if (!window.confirm(`Sync ${companyName}'s subscription end date from Stripe?\n\nThis pulls the real billing date from Stripe and updates our records — won't move the billing cycle.`)) return;
    try {
      const res = await axios.post(`${API}/subscriptions/admin/${companyId}/sync-from-stripe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data?.message || 'Synced from Stripe');
      fetchOverdueSubscriptions();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Stripe sync failed');
    }
  };

  const openChinaQuoteDialog = (order) => {
    setSelectedChinaOrder(order);
    setChinaQuoteForm({
      china_cost: order.china_cost || '',
      markup_amount: order.markup_amount || '',
      shipping_cost: order.shipping_cost || '',
      quote_notes: order.quote_notes || ''
    });
    setShowChinaQuoteDialog(true);
  };

  const handleSendChinaQuote = async () => {
    if (!chinaQuoteForm.china_cost || !chinaQuoteForm.markup_amount) {
      toast.error('Please enter supplier cost and markup amount');
      return;
    }
    
    setSubmittingChinaQuote(true);
    try {
      await axios.post(`${API}/group-orders/${selectedChinaOrder.id}/quote`, {
        china_cost: parseFloat(chinaQuoteForm.china_cost),
        markup_amount: parseFloat(chinaQuoteForm.markup_amount),
        shipping_cost: parseFloat(chinaQuoteForm.shipping_cost) || 0,
        quote_notes: chinaQuoteForm.quote_notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Quote sent successfully');
      setShowChinaQuoteDialog(false);
      fetchChinaOrders();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send quote'); toast.error(msg);
    } finally {
      setSubmittingChinaQuote(false);
    }
  };

  const handleUpdateChinaOrderStatus = async (orderId, newStatus, additionalData = {}) => {
    try {
      await axios.patch(`${API}/group-orders/${orderId}`, {
        status: newStatus,
        ...additionalData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order updated');
      fetchChinaOrders();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update order'); toast.error(msg);
    }
  };

  const handleDeleteChinaOrder = async (order) => {
    if (!window.confirm(`Permanently delete trade quote request ${order.reference_number || ''}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/group-orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trade quote request deleted');
      fetchChinaOrders();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete request');
    }
  };

  // Show the artwork reminder dialog only once the urgent tasks dialog is closed
  useEffect(() => {
    if (pendingArtworkPopup && !showUrgentPopup) {
      setShowArtworkReminderPopup(true);
      setPendingArtworkPopup(false);
    }
  }, [pendingArtworkPopup, showUrgentPopup]);

  useEffect(() => {
    fetchOrders();
    fetchAllPayments();
    fetchUsers();
    if (['staff', 'workshop', 'manager', 'admin', 'super_admin', 'company_admin', 'demo'].includes(user?.role)) {
      fetchPendingTasksCount();
      fetchUrgentTasks();
      fetchFittingReminders();
      fetchDueMaintenance();
    }
    if (['admin', 'super_admin', 'company_admin', 'manager', 'demo'].includes(user?.role)) {
      fetchWorktopsCount();
      fetchEditingLocks(); // Fetch editing locks
      fetchAgreementSummary();
      fetchAwaitingReply(); // Family pages awaiting a reply
    }
    // Fetch wholesale group orders for all admin users: SIS admins to manage
    // requests, super_admins to see all, and regular masons so we can pop a
    // toast when their quote is ready.
    if (['admin', 'super_admin', 'company_admin', 'manager'].includes(user?.role)) {
      fetchChinaOrders();
    }
    // Fetch companies list for super admin filter
    if (user?.role === 'super_admin') {
      fetchCompaniesList();
      fetchOverdueSubscriptions();
    }
    
    // Poll for editing locks every 30 seconds
    const lockPollInterval = setInterval(fetchEditingLocks, 30000);
    return () => clearInterval(lockPollInterval);
  }, []);
  
  // Refetch orders and users when company filter changes (super admin only)
  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchOrders();
      // Also refetch users for the selected company filter
      if (selectedCompanyFilter && selectedCompanyFilter !== 'all') {
        fetchUsers(selectedCompanyFilter);
      } else {
        fetchUsers(); // Default to own company
      }
    }
  }, [selectedCompanyFilter]);
  
  const fetchCompaniesList = async () => {
    try {
      const response = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompaniesList(response.data || []);
    } catch (error) {
      console.error('Failed to fetch companies list');
    }
  };

  const fetchUsers = async (companyId = null) => {
    try {
      // Build URL with optional company_id parameter for proper data isolation
      let url = `${API}/users`;
      if (companyId) {
        url += `?company_id=${companyId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.filter(u => ['admin', 'super_admin', 'company_admin', 'staff'].includes(u.role)));
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchUrgentTasks = async () => {
    if (remindersSnoozed()) return;
    try {
      const response = await axios.get(`${API}/tasks/urgent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.length > 0) {
        setUrgentTasks(response.data);
        setShowUrgentPopup(true);
        // If the artwork reminder dialog is already open, close it and requeue —
        // stacked dialogs make the one underneath unclickable
        setShowArtworkReminderPopup(prev => {
          if (prev) setPendingArtworkPopup(true);
          return false;
        });
      }
    } catch (error) {
      console.error('Failed to fetch urgent tasks');
    }
  };

  const fetchFittingReminders = async () => {
    if (remindersSnoozed()) return;
    try {
      const response = await axios.get(`${API}/orders/fitting-reminders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.total_alerts > 0) {
        setFittingReminders(response.data);
        // Show popup if there are ready-to-fit orders
        if (response.data.ready_to_fit.length > 0) {
          setShowFittingPopup(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch fitting reminders');
    }
  };

  const fetchDueMaintenance = async () => {
    // Staff, workshop and admins can all see due maintenance
    if (!['admin', 'super_admin', 'company_admin', 'manager', 'staff', 'workshop'].includes(user?.role)) return;
    if (remindersSnoozed()) return;
    try {
      const response = await axios.get(`${API}/maintenance-schedules/due`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const items = response.data || [];
      if (items.length > 0) {
        setDueMaintenance(items);
        setShowMaintenancePopup(true);
      }
    } catch (error) {
      // 403 for staff/demo is expected — swallow
      if (error.response?.status !== 403) {
        console.error('Failed to fetch due maintenance', error);
      }
    }
  };

  const [completingMaintenance, setCompletingMaintenance] = useState(null);
  const completeMaintenance = async (item) => {
    setCompletingMaintenance(item.id);
    try {
      const res = await axios.post(`${API}/maintenance-schedules/${item.id}/complete`,
        { notes: 'Marked complete from dashboard' },
        { headers: { Authorization: `Bearer ${token}` } });
      const nextDue = res.data?.next_due_date;
      toast.success(`${item.machine_name || item.name || 'Maintenance'} completed${nextDue ? ` — next due ${new Date(nextDue).toLocaleDateString('en-GB')}` : ''}`);
      setDueMaintenance(prev => {
        const remaining = prev.filter(m => m.id !== item.id);
        if (remaining.length === 0) setShowMaintenancePopup(false);
        return remaining;
      });
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only admins can mark maintenance complete — ask an admin to sign it off.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to mark maintenance complete');
      }
    } finally {
      setCompletingMaintenance(null);
    }
  };

  const fetchPendingTasksCount = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: 'pending' }
      });
      setPendingTasksCount(response.data.length);
    } catch (error) {
      console.error('Failed to fetch tasks count');
    }
  };

  const fetchWorktopsCount = async () => {
    try {
      const response = await axios.get(`${API}/worktops`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Count only active orders (not completed and not archived)
      const activeCount = response.data.filter(order => 
        order.status !== 'completed' && !order.is_archived
      ).length;
      setWorktopsCount(activeCount);
    } catch (error) {
      console.error('Failed to fetch worktops count');
    }
  };

  const fetchAgreementSummary = async () => {
    try {
      const response = await axios.get(`${API}/agreements/dashboard-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreementSummary(response.data || { enabled: false, statuses: {} });
    } catch (error) {
      setAgreementSummary({ enabled: false, statuses: {} });
    }
  };

  // ---- On-hold / Resume helpers ---------------------------------------
  const handleHoldOrder = async (order) => {
    const reason = window.prompt(
      `Place order #${order.order_number} on hold?\n\nOptional reason (visible on the card):`,
      ''
    );
    if (reason === null) return; // user cancelled
    try {
      await axios.post(`${API}/orders/${order.id}/hold`, { reason: reason.trim() || null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Order #${order.order_number} paused — alerts suppressed.`);
      fetchOrders(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to put order on hold');
    }
  };

  const handleResumeOrder = async (order) => {
    if (!window.confirm(
      `Resume order #${order.order_number} (${order.client_name})?\n\n` +
      `All alerts and reminders will re-activate. Are you happy to resume the order process?`
    )) return;
    try {
      await axios.post(`${API}/orders/${order.id}/resume`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Order #${order.order_number} resumed.`);
      fetchOrders(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to resume order');
    }
  };

  const handleStoneInStock = async (order) => {
    if (!window.confirm(
      `Mark order #${order.order_number} (${order.client_name}) as STONE IN STOCK?\n\n` +
      `This will turn the "Stone Ordered" light green and email the client to say the ` +
      `memorial stone has arrived and work will begin as soon as possible.`
    )) return;
    try {
      const res = await axios.post(`${API}/orders/${order.id}/stone-in-stock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data?.message || `Order #${order.order_number} marked Stone In Stock.`);
      fetchOrders(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to mark Stone In Stock');
    }
  };

  const fetchOrders = async (loadMore = false, attempt = 0) => {
    let willRetry = false;
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const skip = loadMore ? orders.length : 0;
      const params = {
        limit: 100,
        skip: skip,
        include_count: !loadMore,  // Only get count on first load
        include_alerts: true  // Include alert information
      };
      
      // Super admin company filter options
      if (user?.role === 'super_admin') {
        if (selectedCompanyFilter === 'all') {
          params.view_all_companies = true;
        } else {
          // Specific company selected (including 'set-in-stone' as default)
          params.filter_company_id = selectedCompanyFilter;
          params.view_all_companies = true; // Need this to bypass own company filter
        }
      }
      
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...params, include_events: true }  // Request events in same call
      });

      const newOrders = response.data.orders;
      setOrdersLoadFailed(false);
      
      // Events are now included in the response, no need for additional API calls
      const ordersWithEvents = newOrders.map(order => ({
        ...order,
        events: order.events || []
      }));
      
      if (loadMore) {
        setOrders(prev => [...prev, ...ordersWithEvents]);
      } else {
        setOrders(ordersWithEvents);
        if (response.data.pagination.total !== undefined) {
          setTotalCount(response.data.pagination.total);
        }
        
        // Check for artwork reminders (only for admin users)
        if (['admin', 'super_admin', 'company_admin', 'manager'].includes(user?.role)) {
          const reminders = ordersWithEvents.filter(order => 
            order.alerts?.artwork_reminder_due === true
          );
          setArtworkReminders(reminders);
          if (reminders.length > 0 && !remindersSnoozed()) {
            // Queue instead of opening directly — never stack on top of the urgent tasks dialog
            setPendingArtworkPopup(true);
          }
        }
      }
      
      setHasMore(response.data.pagination.has_more);
    } catch (error) {
      // Auto-retry: the server may be busy (timeouts) — retry twice with backoff
      // before showing a failure, and NEVER wipe orders already on screen.
      if (!loadMore && attempt < 2) {
        willRetry = true;
        setTimeout(() => fetchOrders(false, attempt + 1), 2000 * (attempt + 1));
      } else {
        setOrdersLoadFailed(true);
        toast.error('Failed to fetch orders — the server may be busy. Retrying may help.');
      }
    } finally {
      if (!willRetry) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const fetchAllPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (error) {
      console.error('Failed to fetch payments');
    }
  };

  // Calculate balance for an order
  const calculateBalance = (order) => {
    // Get payments for this order
    const orderPayments = payments.filter(p => p.order_id === order.id);
    const totalPaid = orderPayments.reduce((sum, p) => sum + safeNumber(p.amount), 0);
    
    // Use the stored total from the order (already includes all items, VAT, etc.)
    // This ensures consistency with what was quoted/invoiced
    let totalAmount = safeNumber(order.total);
    
    // Fallback: if total is not stored, calculate it
    if (!totalAmount || totalAmount === 0) {
      let subtotal = safeNumber(order.price);
      
      // Add sundries to subtotal
      if (order.sundries && order.sundries.length > 0) {
        subtotal += order.sundries.reduce((sum, s) => sum + safeNumber(s.price), 0);
      }
      
      // Calculate VAT on subtotal
      const vatRate = safeNumber(order.vat_rate, 20);
      const vat = subtotal * (vatRate / 100);
      
      // Add cemetery fee (VAT exempt)
      const vatExemptTotal = safeNumber(order.cemetery_fee);
      
      // Total amount = subtotal + VAT + VAT exempt items
      totalAmount = subtotal + vat + vatExemptTotal;
    }
    
    // Balance = total amount - total paid
    return totalAmount - totalPaid;
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        order_id: taskForm.order_id || null,
        title: taskForm.title,
        description: taskForm.description,
        priority: taskForm.priority,
        assigned_to: taskForm.assigned_to || null,
        timer_duration_hours: taskForm.timer_duration_hours ? parseInt(taskForm.timer_duration_hours) : null,
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null
      };

      await axios.post(`${API}/tasks`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Task created successfully');
      setTaskDialog(false);
      setTaskForm({
        order_id: '',
        title: '',
        description: '',
        priority: 'normal',
        assigned_to: '',
        timer_duration_hours: '',
        due_date: ''
      });
      fetchPendingTasksCount();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleToggleStencil = async (orderId, currentStatus) => {
    try {
      const response = await axios.patch(`${API}/orders/${orderId}/toggle-stencil`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the order in the local state
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, stencil_cut: response.data.stencil_cut } : order
      ));
      
      toast.success(response.data.stencil_cut ? 'Stencil marked as cut' : 'Stencil unmarked');
    } catch (error) {
      toast.error('Failed to toggle stencil status');
    }
  };

  const handleStatusClick = (status) => {
    setSelectedStatus(status);
    setSearchTerm(''); // Clear search when filtering by status
  };

  const handleOrderTypeClick = (orderType) => {
    setSelectedOrderType(orderType);
    setSearchTerm(''); // Clear search when filtering by order type
  };

  const filteredOrders = orders.filter(order => {
    // Filter by status if selected
    if (selectedStatus) {
      if (selectedStatus === 'needs-artwork') {
        // Virtual bucket: any order awaiting artwork OR requiring changes
        const NEEDS = ['accepted', 'deposit-taken', 'changes-requested'];
        if (!NEEDS.includes(order.status)) return false;
        // Restorations, maintenance and grave vaults never need artwork
        if (['restoration', 'maintenance', 'grave vault'].includes(
          (order.order_type || '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim())) return false;
      } else if (order.status !== selectedStatus) {
        return false;
      }
    }
    // Filter by order type if selected
    if (selectedOrderType && order.order_type !== selectedOrderType) {
      return false;
    }
    // Filter by search term
    if (searchTerm) {
      const q = searchTerm.toLowerCase().trim();
      if (!q) return true;
      const safe = (v) => String(v ?? '').toLowerCase();
      // Smart order-number match: strip any leading letters from both sides so
      // "SIS26016", "sis26016", "26016" all match order_number "SIS26016" or
      // legacy "26016". Guard against the empty-strip case (all-letters search)
      // matching every order.
      const stripPrefix = (s) => safe(s).replace(/^[a-z]+/i, '');
      const orderNum = safe(order.order_number);
      const qStripped = stripPrefix(q);
      const orderNumStripped = stripPrefix(orderNum);
      const orderNumMatches =
        orderNum.includes(q) ||
        (qStripped && orderNumStripped && orderNumStripped.includes(qStripped));
      // Searchable fields: customer, stone type/colour/shape, cemetery and the
      // name on the memorial (all inscription variants)
      const searchable = [
        order.client_name,
        order.stone_type,
        order.stone_colour,
        order.stone_shape,
        order.cemetery,
        order.inscription,
        order.inscription_left,
        order.inscription_right,
        order.existing_inscription,
        order.new_inscription,
      ];
      return orderNumMatches || searchable.some(f => safe(f).includes(q));
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Demo Tour */}
      {showTour && <DemoTour onComplete={() => setShowTour(false)} />}
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Memorial Orders</h1>
                <p className="text-xs sm:text-sm text-slate-500">Dashboard</p>
              </div>
            </div>
            <div className={`flex items-center gap-1 sm:gap-2 ${user?.role === 'super_admin' ? 'flex-wrap justify-end' : ''}`}>
              <div className="hidden sm:flex items-center space-x-2 px-4 py-2 bg-slate-100 rounded-lg">
                <User className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">{user?.name}</span>
                <Badge variant="secondary" className="ml-2">{user?.role}</Badge>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/my-account')}
                data-testid="my-account-btn"
                className="px-2 sm:px-3"
              >
                <User className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">My Account</span>
              </Button>
              {(user?.role === 'staff' || user?.role === 'workshop' || user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'demo') && (
                <>
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager' || user?.role === 'demo') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/settings')} 
                      className="px-2 sm:px-3"
                      data-testid="settings-btn"
                    >
                      <Package className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Settings</span>
                    </Button>
                  )}
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager' || user?.role === 'demo') && companyContext?.features?.corel_integration_enabled === true && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/corel-integration')}
                      className="px-2 sm:px-3 border-fuchsia-400/30 text-fuchsia-700 hover:bg-fuchsia-50"
                      data-testid="corel-integration-btn"
                    >
                      <Palette className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Corel</span>
                    </Button>
                  )}
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager') && user?.role !== 'demo' && !user?.is_demo && companyContext?.features?.worktops_enabled !== false && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/materials')} 
                      className="px-2 sm:px-3"
                      data-testid="materials-btn"
                    >
                      <Palette className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Materials</span>
                    </Button>
                  )}
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager' || user?.role === 'demo') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/stock')} 
                      className="px-2 sm:px-3"
                      data-testid="stock-btn"
                    >
                      <Boxes className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Stock</span>
                    </Button>
                  )}
                  {/* Group Ordering - available to all memorial companies */}
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager' || user?.role === 'demo') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/group-ordering')} 
                      className="px-2 sm:px-3 border-blue-300 hover:bg-blue-50 relative"
                      data-testid="group-ordering-btn"
                    >
                      <Ship className="w-4 h-4 sm:mr-2 text-blue-600" />
                      <span className="hidden sm:inline">Trade Wholesale</span>
                      {pendingChinaOrdersCount > 0 && (
                        <span
                          data-testid="trade-wholesale-pending-badge"
                          className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none ring-2 ring-white animate-pulse"
                          title={`${pendingChinaOrdersCount} new wholesale quote request(s) waiting`}
                        >
                          {pendingChinaOrdersCount > 9 ? '9+' : pendingChinaOrdersCount}
                        </span>
                      )}
                    </Button>
                  )}
                  {/* Designer queue — only visible to the platform designer */}
                  {(user?.role === 'super_admin' || user?.email === 'memorials@setinstonedidcot.co.uk') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/designer-queue')}
                      className="px-2 sm:px-3 border-purple-300 hover:bg-purple-50 relative"
                      data-testid="designer-queue-btn"
                    >
                      <Palette className="w-4 h-4 sm:mr-2 text-purple-600" />
                      <span className="hidden sm:inline">Designer Queue</span>
                      <DesignerQueueBadge token={token} currentUser={user} />
                    </Button>
                  )}
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager' || user?.role === 'demo') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/users')} 
                      className="px-2 sm:px-3"
                      data-testid="users-btn"
                    >
                      <User className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Users</span>
                    </Button>
                  )}
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager' || user?.role === 'demo') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/archived')} 
                      className="px-2 sm:px-3"
                      data-testid="archived-btn"
                    >
                      <Archive className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Archived</span>
                    </Button>
                  )}
                  {(user?.role === 'super_admin' || isSISAdmin) && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/platform-admin')} 
                      className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-300 hover:from-purple-100 hover:to-indigo-100 min-w-[100px]"
                      data-testid="platform-admin-btn"
                    >
                      <Shield className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Platform</span>
                    </Button>
                  )}
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager' || user?.role === 'demo') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/company-settings')} 
                      className="px-2 sm:px-3"
                      data-testid="company-settings-btn"
                    >
                      <Building2 className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">My Company</span>
                    </Button>
                  )}
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/financial-report')} 
                      className="px-2 sm:px-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-300 hover:from-emerald-100 hover:to-teal-100"
                      data-testid="financial-report-btn"
                    >
                      <PoundSterling className="w-4 h-4 sm:mr-2 text-emerald-600" />
                      <span className="hidden sm:inline text-emerald-700">Finances</span>
                    </Button>
                  )}
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/bulk-messages')} 
                      className="px-2 sm:px-3"
                      data-testid="bulk-messages-btn"
                    >
                      <Send className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Messages</span>
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate('/tasks')} 
                    className="relative min-w-[100px]"
                    data-testid="tasks-btn"
                  >
                    <CheckSquare className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Tasks & Calendar</span>
                    {pendingTasksCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {pendingTasksCount}
                      </span>
                    )}
                  </Button>
                  {fittingReminders.total_alerts > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowFittingPopup(true)} 
                      className="relative min-w-[100px] bg-green-50 border-green-300 hover:bg-green-100"
                      data-testid="fitting-btn"
                    >
                      <Calendar className="w-4 h-4 sm:mr-2 text-green-600" />
                      <span className="hidden sm:inline text-green-700">Fitting</span>
                      <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {fittingReminders.total_alerts}
                      </span>
                    </Button>
                  )}
                </>
              )}
              {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager') && user?.role !== 'demo' && !user?.is_demo && companyContext?.features?.worktops_enabled !== false && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/worktops')} 
                  className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 hover:from-amber-100 hover:to-orange-100 relative min-w-[100px]"
                  data-testid="worktops-btn"
                >
                  <Hammer className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Worktops</span>
                  {worktopsCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {worktopsCount}
                    </span>
                  )}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={logout} className="px-2 sm:px-3" data-testid="logout-btn">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Super Admin: Company Filter */}
        {user?.role === 'super_admin' && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">Super Admin View</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-amber-700">Filter by company:</span>
                <Select value={selectedCompanyFilter} onValueChange={setSelectedCompanyFilter}>
                  <SelectTrigger className="w-[220px] bg-white border-amber-300">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="set-in-stone">
                      <span className="font-medium">Set in Stone</span>
                    </SelectItem>
                    <SelectItem value="all">
                      <span className="font-medium text-amber-700">All Companies</span>
                    </SelectItem>
                    {companiesList.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs text-slate-500 border-t mt-1 pt-1">Other companies:</div>
                        {companiesList.filter(c => c.id !== 'set-in-stone').map(company => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                {selectedCompanyFilter !== 'set-in-stone' && (
                  <Badge variant="secondary" className="bg-amber-200 text-amber-800">
                    {selectedCompanyFilter === 'all' 
                      ? 'Viewing all' 
                      : companiesList.find(c => c.id === selectedCompanyFilter)?.name || 'Filtered'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Trade Wholesale Quote Requests Banner — visible to anyone able to
            quote them (super_admin + Set In Stone admins). Animates the bell
            so it's hard to miss when stones are waiting on a quote. */}
        {(user?.role === 'super_admin' || isSISAdmin) && pendingChinaOrdersCount > 0 && (
          <div
            className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-lg shadow-sm"
            data-testid="trade-wholesale-pending-banner"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Ship className="w-6 h-6 text-amber-700" />
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none ring-2 ring-amber-50 animate-pulse">
                    {pendingChinaOrdersCount > 9 ? '9+' : pendingChinaOrdersCount}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-amber-900">
                    {pendingChinaOrdersCount} new wholesale quote request{pendingChinaOrdersCount > 1 ? 's' : ''} waiting
                  </p>
                  <p className="text-xs text-amber-800">Masons are waiting on you — quote them back within 24–48 hours.</p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => navigate('/group-ordering')}
                data-testid="review-pending-requests-btn"
              >
                Review Requests
              </Button>
            </div>
          </div>
        )}

        {/* Overdue Subscriptions Alert (Super Admin only) */}
        {user?.role === 'super_admin' && overdueSubscriptions.length > 0 && (
          <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-lg" data-testid="overdue-subscriptions-alert">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-5 h-5 text-red-600" />
              <span className="text-sm font-bold text-red-800">
                {overdueSubscriptions.length} Overdue Subscription{overdueSubscriptions.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-2">
              {overdueSubscriptions.map((company) => (
                <div key={company.company_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white rounded-md px-3 py-2 border border-red-200">
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-sm text-slate-900">{company.company_name}</span>
                    <span className="text-xs text-red-600 ml-2">
                      {company.days_overdue} day{company.days_overdue !== 1 ? 's' : ''} overdue
                    </span>
                    <span className="text-xs text-slate-500 ml-2">
                      ({company.tier} — £{company.monthly_price?.toFixed(2)}/{company.billing_cycle === 'yearly' ? 'yr' : 'mo'})
                    </span>
                    {company.last_reminder_sent && (
                      <span className="text-xs text-amber-600 ml-2">
                        Reminder sent {new Date(company.last_reminder_sent).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => sendPaymentReminder(company.company_id)}
                      disabled={sendingReminder === company.company_id}
                      className="bg-red-600 hover:bg-red-700 text-white text-xs"
                      data-testid={`send-reminder-${company.company_id}`}
                    >
                      {sendingReminder === company.company_id ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Send className="w-3 h-3 mr-1" />
                      )}
                      Send Reminder
                    </Button>
                    {company.stripe_subscription_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => syncFromStripe(company.company_id, company.company_name)}
                        className="border-blue-600 text-blue-700 hover:bg-blue-50 text-xs"
                        data-testid={`sync-stripe-${company.company_id}`}
                        title="Pulls the real billing date from Stripe (preserves billing cycle)"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Sync from Stripe
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => dismissOverdueReminder(company.company_id, company.company_name)}
                      className="border-slate-400 text-slate-600 hover:bg-slate-100 text-xs"
                      data-testid={`dismiss-overdue-${company.company_id}`}
                      title="Hide this reminder without changing billing dates"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Dismiss Reminder
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="relative w-full sm:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search customer, memorial name, stone, colour, shape or order #…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
              data-testid="search-input"
            />
          </div>
          {(user?.role === 'staff' || user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'demo') && (
            <div className="flex gap-2 w-full sm:w-auto">
              {/* Only show New Order for memorial companies or mixed companies */}
              {companyContext?.features?.memorials_enabled !== false && (
                <Button onClick={() => navigate('/orders/new')} className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700" data-testid="create-order-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Order
                </Button>
              )}
              {/* For worktop-only companies, show New Worktop Quote prominently */}
              {companyContext?.features?.memorials_enabled === false && companyContext?.features?.worktops_enabled && (
                <Button onClick={() => navigate('/worktops/new')} className="flex-1 sm:flex-none bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700" data-testid="create-worktop-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Worktop Quote
                </Button>
              )}
              <Button onClick={() => setTaskDialog(true)} variant="outline" className="flex-1 sm:flex-none border-green-600 text-green-600 hover:bg-green-50" data-testid="create-task-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
            </div>
          )}
        </div>

        {/* Stats Card - Total Active Orders */}
        {!loading && (
          <div className="mb-6">
            <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium mb-1">Total Active Orders</p>
                    <p className="text-4xl font-bold">{totalCount > 0 ? totalCount : orders.length}</p>
                  </div>
                  <div className="bg-white/20 p-4 rounded-lg">
                    <Package className="w-8 h-8" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Status Chart */}
        {!loading && orders.length > 0 && (
          <StatusChart 
            orders={orders} 
            selectedStatus={selectedStatus}
            onStatusClick={handleStatusClick}
          />
        )}

        {/* Order Type Filter */}
        {!loading && orders.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Filter by Order Type</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setSelectedOrderType(null)}
                variant={selectedOrderType === null ? "default" : "outline"}
                size="sm"
                className={selectedOrderType === null ? "bg-blue-600 hover:bg-blue-700" : ""}
              >
                All Types
              </Button>
              <Button
                onClick={() => handleOrderTypeClick('new memorial')}
                variant={selectedOrderType === 'new memorial' ? "default" : "outline"}
                size="sm"
                className={selectedOrderType === 'new memorial' ? "bg-green-600 hover:bg-green-700" : ""}
              >
                New Memorial
              </Button>
              <Button
                onClick={() => handleOrderTypeClick('restoration')}
                variant={selectedOrderType === 'restoration' ? "default" : "outline"}
                size="sm"
                className={selectedOrderType === 'restoration' ? "bg-amber-600 hover:bg-amber-700" : ""}
              >
                Restoration
              </Button>
              <Button
                onClick={() => handleOrderTypeClick('add on inscription')}
                variant={selectedOrderType === 'add on inscription' ? "default" : "outline"}
                size="sm"
                className={selectedOrderType === 'add on inscription' ? "bg-purple-600 hover:bg-purple-700" : ""}
              >
                Add On Inscription
              </Button>
              <Button
                onClick={() => handleOrderTypeClick('maintenance')}
                variant={selectedOrderType === 'maintenance' ? "default" : "outline"}
                size="sm"
                className={selectedOrderType === 'maintenance' ? "bg-orange-600 hover:bg-orange-700" : ""}
              >
                Maintenance
              </Button>
              {companyContext?.features?.grave_vaults_enabled && (
                <Button
                  onClick={() => handleOrderTypeClick('grave vault')}
                  variant={selectedOrderType === 'grave vault' ? "default" : "outline"}
                  size="sm"
                  className={selectedOrderType === 'grave vault' ? "bg-teal-600 hover:bg-teal-700" : ""}
                >
                  Grave Vault
                </Button>
              )}
            </div>
            {selectedOrderType && (
              <p className="text-xs text-slate-500 mt-2">
                Showing: {selectedOrderType} ({filteredOrders.length} {filteredOrders.length === 1 ? 'order' : 'orders'})
              </p>
            )}
          </div>
        )}

        {/* Awaiting reply — family messages needing a response */}
        {isAdmin && awaitingReply.length > 0 && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="awaiting-reply-panel">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-semibold text-amber-800">Awaiting reply from you</span>
              <span className="text-xs bg-amber-500 text-white rounded-full px-2 py-0.5">{awaitingReply.length}</span>
            </div>
            <div className="space-y-2">
              {awaitingReply.map((a) => {
                const wd = workingDaysSince(a.waiting_since);
                const tone = wd >= 4 ? 'bg-red-100 text-red-700 border-red-300' : wd >= 2 ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-white text-slate-600 border-slate-200';
                return (
                  <button key={a.order_id} onClick={() => navigate(`/orders/${a.order_id}`)} className={`w-full text-left flex items-center gap-3 rounded-lg border px-3 py-2 hover:shadow-sm ${tone}`} data-testid={`awaiting-reply-${a.order_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.deceased_name || a.client_name || 'Order'} · #{a.order_number}</div>
                      <div className="text-xs truncate opacity-80">"{a.last_message}"</div>
                    </div>
                    <div className="text-xs whitespace-nowrap font-semibold">{wd === 0 ? 'today' : `${wd} working day${wd === 1 ? '' : 's'}`}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Orders Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-lg text-slate-600">Loading orders...</div>
          </div>
        ) : filteredOrders.length === 0 ? (
          ordersLoadFailed && orders.length === 0 ? (
            <Card className="text-center py-12 border-2 border-red-200 bg-red-50/50 shadow-lg" data-testid="orders-load-error">
              <CardContent className="pt-6">
                <Package className="w-16 h-16 text-red-300 mx-auto mb-4" />
                <p className="text-lg font-medium text-red-700">Couldn't load your orders</p>
                <p className="text-sm text-slate-600 mt-1">Your orders are safe — the server timed out while fetching them.</p>
                <Button onClick={() => { setLoading(true); fetchOrders(); }} className="mt-4 bg-red-600 hover:bg-red-700 text-white" data-testid="orders-retry-btn">
                  Retry Now
                </Button>
              </CardContent>
            </Card>
          ) : (
          <Card className="text-center py-12 border-0 shadow-lg">
            <CardContent className="pt-6">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-lg text-slate-600">No orders found</p>
              {['staff', 'admin', 'super_admin', 'company_admin'].includes(user?.role) && (
                <Button onClick={() => navigate('/orders/new')} className="mt-4" variant="outline">
                  Create your first order
                </Button>
              )}
            </CardContent>
          </Card>
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="orders-grid">
            {filteredOrders.map((order) => {
              // "Needs Design" indicator is only relevant once the job is
              // actually committed. Skip for:
              //   • restoration / maintenance jobs (no design ever)
              //   • orders still at quote stage (not accepted yet)
              //   • orders whose status is already past the artwork stage
              //   • orders that already have artwork uploaded (event-based)
              // Inscription presence is NOT used as a gate.
              // 'changes-requested' is DELIBERATELY NOT in the past-artwork set
              // — that status means the client rejected and staff must redesign.
              const skipDesignCheck = ['restoration', 'maintenance', 'grave vault'].includes(
                (order.order_type || '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim());
              const isStillQuote = order.status === 'quote';

              const events = order.events || [];
              // AUTHORITATIVE artwork presence — read the SAME source as the
              // Progress Tracking traffic lights below (order.artwork_summary,
              // built from the real artwork records). Previously this box guessed
              // from the order STATUS (assuming anything past "application sent"
              // must already have artwork) which wrongly hid the "Needs Design"
              // flag on orders that reached application-approved with no artwork.
              // Fall back to the explicit upload event only if the summary is
              // missing from the payload.
              const artSummaryCard = order.artwork_summary;
              const hasArtwork = artSummaryCard
                ? artSummaryCard.count > 0
                : events.some(e => e.event_type === 'artwork_uploaded' || e.title === 'Artwork uploaded');
              // If the client has requested changes, artwork always needs
              // rework — force needsDesign regardless of any artwork on file.
              const needsRedesign = order.status === 'changes-requested';
              const needsDesign = needsRedesign || (!skipDesignCheck
                && !isStillQuote
                && !hasArtwork
                && !order.alerts?.artwork_overdue);
              const changesRequested = order.status === 'changes-requested';
              
              // Determine card border style
              let cardBorderClass = 'border-0';
              if (changesRequested) {
                cardBorderClass = 'ring-2 ring-red-500 border-red-400';
              } else if (needsDesign) {
                cardBorderClass = 'ring-2 ring-blue-500 border-blue-400';
              }
              
              return (
              <Card
                key={order.id}
                className={`card-hover cursor-pointer shadow-lg hover:shadow-xl transition-all relative overflow-hidden ${cardBorderClass} ${order.on_hold ? 'opacity-70 saturate-50' : ''}`}
                onClick={() => navigate(`/orders/${order.id}`)}
                data-testid={`order-card-${order.id}`}
              >
                {/* On-Hold Banner — sits above every other alert. When on
                    hold, the artwork / cemetery / reminder alerts are
                    suppressed at source by the backend, so they won't render
                    below. */}
                {order.on_hold && (
                  <div className="bg-slate-700 text-white px-3 py-2 text-xs font-semibold flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-amber-300 rounded-full"></span>
                    ⏸ ON HOLD{order.on_hold_reason ? ` — ${order.on_hold_reason}` : ''}
                  </div>
                )}
                {/* Changes Requested Banner */}
                {changesRequested && !order.on_hold && (
                  <div className="bg-red-600 text-white px-3 py-2 text-xs font-semibold animate-pulse">
                    🔴 CHANGES REQUESTED - {order.client_name} - #{order.order_number}
                  </div>
                )}
                {/* Alert Banners */}
                {!order.on_hold && order.alerts?.artwork_overdue && !changesRequested && (
                  <div className="bg-red-600 text-white px-3 py-2 text-xs font-semibold animate-pulse">
                    ⚠️ ARTWORK NEEDED - {order.client_name} - #{order.order_number} - {order.alerts.days_since_order} days
                  </div>
                )}
                {!order.on_hold && order.alerts?.cemetery_approval_overdue && (
                  <div className="bg-orange-500 text-white px-3 py-2 text-xs font-semibold animate-pulse">
                    ⏰ AWAITING CHURCH/CEMETERY APPROVAL - {order.client_name} - #{order.order_number} - {order.alerts.days_since_application} days since application
                  </div>
                )}
                {!order.on_hold && order.alerts?.artwork_reminder_due && (
                  <div className="bg-yellow-500 text-white px-3 py-2 text-xs font-semibold animate-pulse">
                    📧 ARTWORK REMINDER DUE - {order.client_name} - #{order.order_number}
                  </div>
                )}
                
                {/* Needs Artwork Indicator - shows when no artwork uploaded yet */}
                {!order.on_hold && needsDesign && !changesRequested && (
                  <div className="bg-blue-600 text-white px-3 py-1.5 text-xs font-semibold flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    Needs Design
                  </div>
                )}
                
                {/* Being Edited Banner - shows when another user is editing this order */}
                {isAdmin && editingLocks.find(lock => lock.order_id === order.id) && (
                  <div className="bg-purple-600 text-white px-3 py-1.5 text-xs font-semibold flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                    ✏️ Being edited by {editingLocks.find(lock => lock.order_id === order.id)?.user_name}
                  </div>
                )}
                
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className="text-lg">{order.client_name}</CardTitle>
                        {order.order_number && (
                          <Badge className="bg-indigo-100 text-indigo-800 text-xs font-mono px-2 py-0.5">
                            #{order.order_number}
                          </Badge>
                        )}
                        {order.family?.enabled && (order.family.engaged || order.family.link_sent) && (
                          <span
                            title={order.family.engaged ? 'Family page opened / in use by the family' : 'Family page link sent — not opened yet'}
                            data-testid={`family-icon-${order.id}`}
                            className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${order.family.engaged ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}
                          >
                            <Heart className="w-3.5 h-3.5" fill="currentColor" />
                          </span>
                        )}
                      </div>
                      <p className={`text-xs font-medium capitalize ${
                        order.order_type === 'maintenance' ? 'text-orange-600' : 
                        order.order_type === 'restoration' ? 'text-green-600' :
                        order.order_type === 'add on inscription' ? 'text-purple-600' :
                        'text-blue-600'
                      }`}>{order.order_type}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Order Date: {new Date(order.created_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <StatusBadge status={order.status} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {(order.stone_shape || order.stone_type) && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Stone Shape:</span>
                        <span className="font-medium text-slate-900">{order.stone_shape || order.stone_type}</span>
                      </div>
                    )}
                    {order.stone_colour && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Colour:</span>
                        <span className="font-medium text-slate-900">{order.stone_colour}</span>
                      </div>
                    )}
                    {order.stone_code && String(order.stone_code).trim() && String(order.stone_code) !== '0' && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Code:</span>
                        <span className="font-medium text-slate-900">{order.stone_code}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">Cemetery:</span>
                      <span className="font-medium text-slate-900">{order.cemetery}</span>
                    </div>
                    {isAdmin && (
                    <>
                    <div className="flex justify-between pt-2 border-t border-slate-100">
                      <span className="text-slate-500">Stone Price:</span>
                      <span className="font-semibold text-slate-900">£{safeNumber(order.price).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Deposit:</span>
                      <span className="font-medium text-slate-900">£{safeNumber(order.deposit).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-slate-100">
                      <span className="text-slate-500 font-medium">Balance:</span>
                      <span className="font-bold" style={{ color: calculateBalance(order) > 0 ? '#dc2626' : '#16a34a' }}>
                        {calculateBalance(order) <= 0 ? 'PAID ✓' : `£${calculateBalance(order).toFixed(2)}`}
                      </span>
                    </div>
                    </>
                    )}

                    {/* Pause / Resume + Stone In Stock actions */}
                    {isAdmin && (
                      <div className="pt-2 flex justify-between items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleStoneInStock(order); }}
                          className="text-xs px-3 py-1 rounded-full border border-green-300 text-green-700 hover:bg-green-50"
                          data-testid={`stone-in-stock-${order.id}`}
                          title="Mark the stone as arrived — turns the Stone Ordered light green and emails the client"
                        >
                          🪨 Stone In Stock
                        </button>
                        {order.on_hold ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleResumeOrder(order); }}
                            className="text-xs px-3 py-1 rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`resume-order-${order.id}`}
                            title="Resume normal processing and re-enable alerts"
                          >
                            ▶ Resume Order
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleHoldOrder(order); }}
                            className="text-xs px-3 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
                            data-testid={`hold-order-${order.id}`}
                            title="Put on hold — suppresses all alerts and reminders"
                          >
                            ⏸ Place On Hold
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* Traffic Light Status Indicators - Based on Timeline Events */}
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 font-semibold mb-2">Progress Tracking</p>
                      <div className="flex justify-between items-start gap-1">
                        {(() => {
                          const events = order.events || [];
                          // AUTHORITATIVE: summary computed from the actual artwork
                          // records on the order (same data as the Artwork tab).
                          const artSummary = order.artwork_summary;
                          // Check each stage - match actual event titles from backend
                          const artworkCreatedEvt = events.some(e => 
                            e.event_type === 'artwork_uploaded' || 
                            e.title === 'Artwork uploaded' ||
                            (e.event_type === 'status_change' && (e.title?.includes('artwork-created') || e.description?.includes('artwork-created')))
                          );
                          const artworkApprovedEvt = events.some(e => 
                            e.event_type === 'status_change' && 
                            (e.title === 'Artwork Approved' || 
                             e.title === 'All Artwork Approved' ||
                             e.title?.includes('artwork-approved') || 
                             e.description?.includes('artwork-approved'))
                          ) || order.status === 'artwork-approved';
                          // Prefer the real artwork records; fall back to events only
                          // when the summary isn't in the payload.
                          const artworkCreated = artSummary ? artSummary.count > 0 : artworkCreatedEvt;
                          const artworkApproved = artSummary
                            ? (artSummary.count > 0 && artSummary.approved === artSummary.count)
                            : artworkApprovedEvt;
                          const applicationSent = events.some(e => 
                            e.event_type === 'status_change' && 
                            (e.title === 'Application Sent' || 
                             e.title?.includes('application-sent') || 
                             e.description?.includes('application-sent'))
                          );
                          const applicationApproved = events.some(e => 
                            e.event_type === 'status_change' && 
                            (e.title === 'Application Approved' || 
                             e.title?.includes('application-approved') || 
                             e.description?.includes('application-approved'))
                          );
                          const stoneOrdered = events.some(e => 
                            e.event_type === 'status_change' && 
                            (e.title === 'Stone Ordered' || 
                             e.title?.includes('stone-ordered') || 
                             e.description?.includes('stone-ordered'))
                          );
                          
                          // Status-based fallbacks. IMPORTANT: application "sent" and
                          // "approved" are DISCRETE steps that must actually have happened —
                          // they must NEVER be inferred from a later stone status. An order
                          // can be moved straight to "stone ordered" (manual dropdown or the
                          // Order Stone flow) without the application ever being sent/approved,
                          // so stone-ordered+ statuses are deliberately excluded here.
                          const statusAtOrPast = (stages) => stages.includes(order.status);
                          // Only application-stage statuses count as explicit evidence.
                          const APP_SENT_PLUS = ['application-sent', 'application-fee-paid', 'application-approved'];
                          const APP_APPROVED_PLUS = ['application-approved'];
                          const STONE_ORDERED_PLUS = ['stone-ordered', 'stone-in-stock', 'work-started', 'memorial-completed'];
                          
                          // Cumulative ONLY for artwork: if approved, then created is implied
                          const showArtworkCreated = artworkCreated || artworkApproved;
                          const showArtworkApproved = artworkApproved;
                          // Approved implies sent; otherwise require a real event, the order's
                          // own flag, or an explicit application-stage status.
                          const showApplicationApproved = applicationApproved || !!order.application_approved || statusAtOrPast(APP_APPROVED_PLUS);
                          const showApplicationSent = applicationSent || !!order.application_sent || statusAtOrPast(APP_SENT_PLUS) || showApplicationApproved;
                          const showStoneOrdered = stoneOrdered || statusAtOrPast(STONE_ORDERED_PLUS);
                          // Once the stone has arrived (status stone-in-stock or later, or a
                          // stone-in-stock event), the Stone Ordered light's label flips to "In Stock".
                          const stoneInStockEvt = events.some(e =>
                            e.event_type === 'status_change' &&
                            ((e.title || '').includes('stone-in-stock') ||
                             (e.description || '').includes('stone-in-stock')));
                          const showInStock = stoneInStockEvt ||
                            ['stone-in-stock', 'work-started', 'memorial-completed'].includes(order.status);
                          // Restorations, maintenance and grave vaults never need artwork
                          const noArtworkType = ['restoration', 'maintenance', 'grave vault'].includes(
                            (order.order_type || '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim());
                          
                          return (
                            <>
                              {/* Customer Agreement — first, only when the company has agreements enabled AND this order requires one */}
                              {agreementSummary.enabled && agreementSummary.statuses[order.id] && (() => {
                                const st = agreementSummary.statuses[order.id];
                                const isSigned = st === 'signed' || st === 'overridden_paper';
                                const isOverridden = st === 'overridden_not_required';
                                const bg = isSigned ? 'bg-green-500'
                                          : isOverridden ? 'bg-slate-400'
                                          : 'bg-red-500';
                                const label = isSigned ? (st === 'overridden_paper' ? 'Paper signature on file' : 'Agreement signed')
                                             : isOverridden ? 'Marked not required'
                                             : 'Awaiting customer signature';
                                const icon = isSigned ? '✓' : isOverridden ? '–' : '✗';
                                return (
                                  <div className="flex flex-col items-center flex-1"
                                       data-testid={`agreement-light-${order.id}`}>
                                    <div
                                      className={`w-7 h-7 rounded-full flex items-center justify-center ${bg}`}
                                      title={label}
                                    >
                                      <span className="text-white text-xs">{icon}</span>
                                    </div>
                                    <p className="text-[9px] text-center mt-1 text-slate-600 leading-tight">Agreement<br/>Signed</p>
                                  </div>
                                );
                              })()}

                              {/* Artwork Created — not shown for order types that don't need artwork */}
                              {!noArtworkType && (
                              <div className="flex flex-col items-center flex-1">
                                <div 
                                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                    showArtworkCreated ? 'bg-green-500' : order.alerts?.artwork_overdue ? 'bg-red-500 animate-pulse' : 'bg-red-500'
                                  }`}
                                  title={showArtworkCreated ? 'Artwork Created' : order.alerts?.artwork_overdue ? 'OVERDUE!' : 'Not completed'}
                                >
                                  {showArtworkCreated ? (
                                    <span className="text-white text-xs">✓</span>
                                  ) : order.alerts?.artwork_overdue ? (
                                    <span className="text-white text-xs font-bold">!</span>
                                  ) : (
                                    <span className="text-white text-xs">✗</span>
                                  )}
                                </div>
                                <p className="text-[9px] text-center mt-1 text-slate-600 leading-tight">Artwork<br/>Created</p>
                              </div>
                              )}
                              
                              {/* Artwork Approved — not shown for order types that don't need artwork */}
                              {!noArtworkType && (
                              <div className="flex flex-col items-center flex-1">
                                <div 
                                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                    showArtworkApproved ? 'bg-green-500' : order.alerts?.artwork_reminder_due ? 'bg-yellow-500 animate-pulse' : 'bg-red-500'
                                  }`}
                                  title={showArtworkApproved ? 'Artwork Approved' : order.alerts?.artwork_reminder_due ? 'REMINDER DUE!' : 'Not completed'}
                                  data-testid={`artwork-approved-light-${order.id}`}
                                >
                                  {showArtworkApproved ? (
                                    <span className="text-white text-xs">✓</span>
                                  ) : order.alerts?.artwork_reminder_due ? (
                                    <span className="text-white text-xs font-bold">!</span>
                                  ) : (
                                    <span className="text-white text-xs">✗</span>
                                  )}
                                </div>
                                <p className="text-[9px] text-center mt-1 text-slate-600 leading-tight">Artwork<br/>Approved</p>
                              </div>
                              )}
                              
                              {/* Application Sent */}
                              <div className="flex flex-col items-center flex-1">
                                <div 
                                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                    showApplicationSent ? 'bg-green-500' : 'bg-red-500'
                                  }`}
                                  title={showApplicationSent ? 'Application Sent' : 'Not completed'}
                                >
                                  {showApplicationSent ? (
                                    <span className="text-white text-xs">✓</span>
                                  ) : (
                                    <span className="text-white text-xs">✗</span>
                                  )}
                                </div>
                                <p className="text-[9px] text-center mt-1 text-slate-600 leading-tight">Application<br/>Sent</p>
                              </div>
                              
                              {/* Application Approved */}
                              <div className="flex flex-col items-center flex-1">
                                <div 
                                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                    showApplicationApproved ? 'bg-green-500' : order.alerts?.cemetery_approval_overdue ? 'bg-orange-500 animate-pulse' : 'bg-red-500'
                                  }`}
                                  title={showApplicationApproved ? 'Application Approved' : order.alerts?.cemetery_approval_overdue ? 'OVERDUE!' : 'Not completed'}
                                >
                                  {showApplicationApproved ? (
                                    <span className="text-white text-xs">✓</span>
                                  ) : order.alerts?.cemetery_approval_overdue ? (
                                    <span className="text-white text-xs font-bold">!</span>
                                  ) : (
                                    <span className="text-white text-xs">✗</span>
                                  )}
                                </div>
                                <p className="text-[9px] text-center mt-1 text-slate-600 leading-tight">Application<br/>Approved</p>
                              </div>
                              
                              {/* Stone Ordered / In Stock */}
                              <div className="flex flex-col items-center flex-1">
                                <div 
                                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                    showStoneOrdered ? 'bg-green-500' : 'bg-red-500'
                                  }`}
                                  title={showInStock ? 'In Stock' : (showStoneOrdered ? 'Stone Ordered' : 'Not completed')}
                                >
                                  {showStoneOrdered ? (
                                    <span className="text-white text-xs">✓</span>
                                  ) : (
                                    <span className="text-white text-xs">✗</span>
                                  )}
                                </div>
                                <p className="text-[9px] text-center mt-1 text-slate-600 leading-tight">{showInStock ? <>In<br/>Stock</> : <>Stone<br/>Ordered</>}</p>
                              </div>
                              
                              {/* Stencil Cut */}
                              <div className="flex flex-col items-center flex-1">
                                <div 
                                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                    order.stencil_cut ? 'bg-green-500' : 'bg-red-500'
                                  }`}
                                  title={order.stencil_cut ? 'Stencil Cut' : 'Not completed'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleStencil(order.id, order.stencil_cut);
                                  }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {order.stencil_cut ? (
                                    <span className="text-white text-xs">✓</span>
                                  ) : (
                                    <span className="text-white text-xs">✗</span>
                                  )}
                                </div>
                                <p className="text-[9px] text-center mt-1 text-slate-600 leading-tight">Stencil<br/>Cut</p>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )})}
          </div>
        )}

        {/* Load More Button */}
        {!loading && hasMore && (
          <div className="mt-8 text-center">
            <Button
              onClick={() => fetchOrders(true)}
              disabled={loadingMore}
              variant="outline"
              className="px-8 py-3"
            >
              {loadingMore ? 'Loading...' : 'Load More Orders'}
            </Button>
            {totalCount > 0 && (
              <p className="text-sm text-slate-500 mt-2">
                Showing {orders.length} of {totalCount} orders
              </p>
            )}
          </div>
        )}

        {/* China Ordering Section - Only for Set In Stone admins */}
        {isSISAdmin && (
          <div className="mt-8 bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Ship className="w-6 h-6 text-cyan-400" />
                Trade Wholesale Quote Requests
                {pendingChinaOrdersCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                    {pendingChinaOrdersCount} Pending
                  </span>
                )}
              </h2>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={fetchChinaOrders}
                className="text-slate-400 hover:text-white"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            
            {chinaOrders.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No group orders yet</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {chinaOrders.slice(0, 9).map(order => (
                  <div key={order.id} className={`p-4 rounded-lg border ${
                    order.status === 'submitted' ? 'bg-blue-900/20 border-blue-500/50' :
                    order.status === 'quoted' ? 'bg-purple-900/20 border-purple-500/50' :
                    order.status === 'accepted' ? 'bg-green-900/20 border-green-500/50' :
                    order.status === 'ordered' ? 'bg-orange-900/20 border-orange-500/50' :
                    order.status === 'shipped' ? 'bg-cyan-900/20 border-cyan-500/50' :
                    'bg-slate-700/50 border-slate-600'
                  }`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-mono font-semibold text-white text-sm">{order.reference_number}</p>
                        <p className="text-xs text-slate-400">{order.company_name}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        order.status === 'submitted' ? 'bg-blue-600 text-white' :
                        order.status === 'quoted' ? 'bg-purple-600 text-white' :
                        order.status === 'accepted' ? 'bg-green-600 text-white' :
                        order.status === 'ordered' ? 'bg-orange-600 text-white' :
                        order.status === 'shipped' ? 'bg-cyan-600 text-white' :
                        'bg-slate-600 text-white'
                      }`}>
                        {order.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    
                    <div className="text-xs text-slate-300 mb-2">
                      {order.stone_colour} {order.stone_type} • {order.components?.length || 0} component(s)
                    </div>
                    
                    {order.quote_amount && (
                      <div className="text-sm text-green-400 mb-2">
                        £{parseFloat(order.quote_amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        {order.deposit_paid && <span className="ml-1 text-green-300">(Dep. ✓)</span>}
                      </div>
                    )}
                    
                    {order.eta && (
                      <div className="text-xs text-orange-400 mb-2">
                        ETA: {new Date(order.eta).toLocaleDateString('en-GB')}
                      </div>
                    )}
                    
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {order.status === 'submitted' && (
                        <Button 
                          size="sm" 
                          onClick={() => openChinaQuoteDialog(order)}
                          className="bg-purple-600 hover:bg-purple-700 h-7 text-xs"
                        >
                          <PoundSterling className="w-3 h-3 mr-1" />
                          Quote
                        </Button>
                      )}
                      {order.status === 'quoted' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openChinaQuoteDialog(order)}
                          className="text-purple-400 border-purple-500 hover:bg-purple-900/50 h-7 text-xs"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                      )}
                      {order.status === 'accepted' && !order.deposit_paid && (
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateChinaOrderStatus(order.id, 'ordered', { deposit_paid: true })}
                          className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Deposit Paid
                        </Button>
                      )}
                      {order.status === 'ordered' && (
                        <Button 
                          size="sm" 
                          onClick={() => {
                            const eta = prompt('Enter ETA (YYYY-MM-DD):');
                            if (eta) handleUpdateChinaOrderStatus(order.id, 'shipped', { eta });
                          }}
                          className="bg-cyan-600 hover:bg-cyan-700 h-7 text-xs"
                        >
                          <Ship className="w-3 h-3 mr-1" />
                          Shipped
                        </Button>
                      )}
                      {order.status === 'shipped' && (
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateChinaOrderStatus(order.id, 'arrived')}
                          className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                        >
                          <Package className="w-3 h-3 mr-1" />
                          Arrived
                        </Button>
                      )}
                      {order.status === 'arrived' && (
                        <>
                          {!order.balance_paid && (
                            <Button 
                              size="sm" 
                              onClick={() => handleUpdateChinaOrderStatus(order.id, 'arrived', { balance_paid: true })}
                              className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Balance Paid
                            </Button>
                          )}
                          {order.balance_paid && (
                            <Button 
                              size="sm" 
                              onClick={() => handleUpdateChinaOrderStatus(order.id, 'ready_for_collection')}
                              className="bg-lime-600 hover:bg-lime-700 h-7 text-xs"
                            >
                              <Truck className="w-3 h-3 mr-1" />
                              Ready
                            </Button>
                          )}
                        </>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openChinaQuoteDialog(order)}
                        className="text-slate-400 border-slate-500 hover:bg-slate-700 h-7 text-xs"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </Button>
                      {order.status === 'cancelled' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDeleteChinaOrder(order)}
                          className="text-red-400 border-red-500/60 hover:bg-red-900/40 h-7 text-xs"
                          data-testid={`delete-trade-quote-${order.id}`}
                          title="Delete this cancelled/declined request"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {chinaOrders.length > 9 && (
              <p className="text-center text-slate-400 text-sm mt-4">
                Showing 9 of {chinaOrders.length} orders. View all in Platform Admin.
              </p>
            )}
          </div>
        )}
      </main>

      {/* China Quote Dialog */}
      <Dialog open={showChinaQuoteDialog} onOpenChange={setShowChinaQuoteDialog}>
        <DialogContent className="sm:max-w-2xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ship className="w-5 h-5 text-cyan-400" />
              {selectedChinaOrder?.status === 'submitted' ? 'Send Quote' : 'Order Details'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedChinaOrder?.reference_number} - {selectedChinaOrder?.company_name}
            </DialogDescription>
          </DialogHeader>

          {selectedChinaOrder && (
            <div className="space-y-4 py-4">
              {/* Stone Details */}
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <h4 className="font-semibold text-cyan-400 mb-2">Stone Details</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Colour:</span>
                    <span className="ml-1 text-white">{selectedChinaOrder.stone_colour}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Type:</span>
                    <span className="ml-1 text-white">{selectedChinaOrder.stone_type || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Finish:</span>
                    <span className="ml-1 text-white">{selectedChinaOrder.stone_finish || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Components */}
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <h4 className="font-semibold text-cyan-400 mb-2">Components</h4>
                <div className="space-y-2">
                  {selectedChinaOrder.components?.map((comp, idx) => (
                    <div key={idx} className="p-2 bg-slate-600/50 rounded flex justify-between items-center">
                      <span className="text-white">
                        {comp.component_type === 'custom' ? comp.custom_description : comp.component_type}
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-cyan-500/30 text-cyan-200 text-xs font-semibold">× {comp.quantity || 1}</span>
                      </span>
                      <span className="text-slate-300 text-sm">
                        {comp.height}" × {comp.width}" × {comp.depth}"
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Customer Notes */}
              {selectedChinaOrder.customer_notes && (
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <h4 className="font-semibold text-cyan-400 mb-2">Customer Notes</h4>
                  <p className="text-slate-300 text-sm">{selectedChinaOrder.customer_notes}</p>
                </div>
              )}

              {/* Quote Form - Show for submitted/quoted orders */}
              {['submitted', 'quoted'].includes(selectedChinaOrder.status) && (
                <div className="p-4 bg-purple-900/30 rounded-lg border border-purple-500/50">
                  <h4 className="font-semibold text-purple-400 mb-3">Quote Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Supplier Cost (£) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={chinaQuoteForm.china_cost}
                        onChange={(e) => setChinaQuoteForm(prev => ({ ...prev, china_cost: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., 450.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Markup (£) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={chinaQuoteForm.markup_amount}
                        onChange={(e) => setChinaQuoteForm(prev => ({ ...prev, markup_amount: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., 150.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Shipping (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={chinaQuoteForm.shipping_cost}
                        onChange={(e) => setChinaQuoteForm(prev => ({ ...prev, shipping_cost: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., 100.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Total Quote</Label>
                      <div className="px-3 py-2 bg-slate-600 rounded-md text-xl font-bold text-green-400">
                        £{((parseFloat(chinaQuoteForm.china_cost) || 0) + (parseFloat(chinaQuoteForm.markup_amount) || 0) + (parseFloat(chinaQuoteForm.shipping_cost) || 0)).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <Label className="text-slate-300">Quote Notes</Label>
                    <Textarea
                      value={chinaQuoteForm.quote_notes}
                      onChange={(e) => setChinaQuoteForm(prev => ({ ...prev, quote_notes: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="Any notes about the quote..."
                      rows={2}
                    />
                  </div>
                </div>
              )}

              {/* Order Status - Show for accepted+ orders */}
              {!['submitted', 'quoted', 'cancelled'].includes(selectedChinaOrder.status) && (
                <div className="p-4 bg-green-900/30 rounded-lg border border-green-500/50">
                  <h4 className="font-semibold text-green-400 mb-3">Order Status</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Quote:</span>
                      <span className="ml-1 text-white">£{parseFloat(selectedChinaOrder.quote_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Deposit:</span>
                      <span className={`ml-1 ${selectedChinaOrder.deposit_paid ? 'text-green-400' : 'text-amber-400'}`}>
                        {selectedChinaOrder.deposit_paid ? '✓ Paid' : 'Pending'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400">Balance:</span>
                      <span className={`ml-1 ${selectedChinaOrder.balance_paid ? 'text-green-400' : 'text-amber-400'}`}>
                        {selectedChinaOrder.balance_paid ? '✓ Paid' : 'Pending'}
                      </span>
                    </div>
                    {selectedChinaOrder.eta && (
                      <div>
                        <span className="text-slate-400">ETA:</span>
                        <span className="ml-1 text-white">{new Date(selectedChinaOrder.eta).toLocaleDateString('en-GB')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button variant="outline" onClick={() => setShowChinaQuoteDialog(false)} className="text-slate-300 border-slate-600">
              Cancel
            </Button>
            {selectedChinaOrder && ['submitted', 'quoted'].includes(selectedChinaOrder.status) && (
              <Button 
                onClick={handleSendChinaQuote} 
                disabled={submittingChinaQuote}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {submittingChinaQuote ? 'Sending...' : selectedChinaOrder.status === 'quoted' ? 'Update Quote' : 'Send Quote'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
            <DialogDescription>
              Create a standalone task or attach it to an order
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="task-order">Attach to Order (Optional)</Label>
              <select
                id="task-order"
                value={taskForm.order_id}
                onChange={(e) => setTaskForm({ ...taskForm, order_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- No order (Standalone task) --</option>
                {orders.map(order => (
                  <option key={order.id} value={order.id}>
                    {order.client_name} - {order.stone_type} ({order.cemetery})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-title">Task Title *</Label>
              <Input
                id="task-title"
                placeholder="e.g., Design memorial artwork"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="task-description">Description *</Label>
              <Textarea
                id="task-description"
                placeholder="Task details..."
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-priority">Priority</Label>
                <select
                  id="task-priority"
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="task-assigned">Assign To</Label>
                <select
                  id="task-assigned"
                  value={taskForm.assigned_to}
                  onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select user...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.email}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-timer">Timer Duration (Hours)</Label>
                <Input
                  id="task-timer"
                  type="number"
                  min="1"
                  placeholder="e.g., 24, 48, 72"
                  value={taskForm.timer_duration_hours}
                  onChange={(e) => setTaskForm({ ...taskForm, timer_duration_hours: e.target.value })}
                />
                <p className="text-xs text-slate-500">Task becomes urgent when timer expires</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="task-due">Due Date (Optional)</Label>
                <Input
                  id="task-due"
                  type="date"
                  value={taskForm.due_date ? taskForm.due_date.split('T')[0] : ''}
                  onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setTaskDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                <Plus className="w-4 h-4 mr-2" />
                Create Task
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Urgent Tasks Popup */}
      <Dialog open={showUrgentPopup} onOpenChange={setShowUrgentPopup}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <DialogTitle className="text-2xl">Urgent Tasks!</DialogTitle>
                <DialogDescription>
                  You have {urgentTasks.length} task{urgentTasks.length > 1 ? 's' : ''} with expired timers
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {urgentTasks.map((task) => (
              <Card key={task.id} className="border-red-200 bg-red-50">
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-900">{task.title}</h3>
                      <p className="text-sm text-red-700 mt-1">{task.description}</p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="destructive">URGENT</Badge>
                        <Badge variant="outline">{task.priority}</Badge>
                        {task.assigned_to && (
                          <Badge variant="secondary">Assigned to: {task.assigned_to}</Badge>
                        )}
                      </div>
                      {task.timer_duration_hours && (
                        <p className="text-xs text-red-600 mt-2">
                          Timer expired (was set for {task.timer_duration_hours} hours)
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={snoozeRemindersForToday} className="border-slate-300 text-slate-600" data-testid="snooze-reminders-today-btn">
              Snooze Reminders for Today
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowUrgentPopup(false)}>
                Close
              </Button>
              <Button onClick={() => {
                setShowUrgentPopup(false);
                navigate('/tasks');
              }}>
                View All Tasks & Calendar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Maintenance Due Popup */}
      <Dialog open={showMaintenancePopup} onOpenChange={setShowMaintenancePopup}>
        <DialogContent className="max-w-2xl" data-testid="maintenance-due-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl text-amber-600">🔧 Maintenance Due</DialogTitle>
            <DialogDescription>
              {dueMaintenance.length} maintenance item{dueMaintenance.length === 1 ? '' : 's'} {dueMaintenance.length === 1 ? 'is' : 'are'} due or overdue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {dueMaintenance.map(item => (
              <div key={item.id} className="border border-amber-200 rounded-lg p-3 bg-amber-50" data-testid={`maintenance-item-${item.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{item.machine_name || item.name || 'Maintenance item'}</p>
                    <p className="text-sm text-slate-600">
                      {item.maintenance_type ? `${item.maintenance_type} • ` : ''}
                      Due: {item.next_due_date ? new Date(item.next_due_date).toLocaleDateString('en-GB') : '—'}
                    </p>
                    {item.notes && <p className="text-xs text-slate-500 mt-1">{item.notes}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => completeMaintenance(item)}
                      disabled={completingMaintenance === item.id}
                      data-testid={`maintenance-complete-${item.id}`}
                    >
                      {completingMaintenance === item.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <><Check className="w-3.5 h-3.5 mr-1" /> Mark Completed</>}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`${API}/maintenance-schedules/${item.id}/job-sheet?token=${token}`, '_blank')}
                      data-testid={`maintenance-jobsheet-${item.id}`}
                    >
                      Job Sheet
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate('/settings?tab=maintenance')}
                      data-testid={`maintenance-open-${item.id}`}
                    >
                      Open
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowMaintenancePopup(false)} data-testid="maintenance-dismiss-btn">
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Artwork Reminder Popup */}
      <Dialog open={showArtworkReminderPopup} onOpenChange={setShowArtworkReminderPopup}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-orange-600">📧 Artwork Approval Reminders Due</DialogTitle>
            <DialogDescription>
              The following orders have artwork awaiting approval for 7+ days. Send reminder emails?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {artworkReminders.map(order => (
              <div key={order.id} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold">{order.client_name}</h4>
                    <p className="text-sm text-gray-600">#{order.order_number} - {order.stone_shape || order.stone_type}</p>
                    <p className="text-xs text-orange-600 mt-1">
                      Artwork sent {order.alerts?.days_since_artwork} days ago
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <Button
                      size="sm"
                      onClick={async () => {
                        let sent = false;
                        try {
                          const res = await axios.post(`${API}/orders/${order.id}/send-artwork-reminder`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          if (res.data?.success) {
                            toast.success(`Reminder sent to ${order.client_name}`);
                            sent = true;
                          } else {
                            toast.error(res.data?.message || 'Delivery failed — snoozed for 7 days so it won\u2019t nag you again.');
                          }
                        } catch (error) {
                          const detail = error.response?.data?.detail;
                          toast.error(typeof detail === 'string' ? detail : (error.response?.data?.message || 'Delivery failed — snoozed for 7 days so it won\u2019t nag you again.'));
                        }
                        // Whether SMTP succeeded or not, the backend now
                        // writes a timeline event so the popup will not
                        // re-appear for this order for 7 days. Remove it from
                        // the popup so the user isn't stuck clicking the same
                        // failing row over and over.
                        setArtworkReminders(prev => {
                          const next = prev.filter(o => o.id !== order.id);
                          if (next.length === 0) setShowArtworkReminderPopup(false);
                          return next;
                        });
                        void sent;
                      }}
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid={`send-artwork-reminder-${order.id}`}
                    >
                      Send Reminder
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs h-7 px-2 text-slate-600"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/orders/${order.id}/snooze-artwork-reminder`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Snoozed for 7 days');
                        } catch (_) {
                          toast.error('Failed to snooze');
                          return;
                        }
                        setArtworkReminders(prev => {
                          const next = prev.filter(o => o.id !== order.id);
                          if (next.length === 0) setShowArtworkReminderPopup(false);
                          return next;
                        });
                      }}
                      title="Already reminded outside the app? Dismiss this popup for 7 days."
                      data-testid={`snooze-artwork-reminder-${order.id}`}
                    >
                      Already done — snooze 7d
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowArtworkReminderPopup(false)}>
                Close
              </Button>
              <Button variant="outline" onClick={snoozeRemindersForToday} className="border-slate-300 text-slate-600" data-testid="snooze-reminders-today-artwork-btn">
                Snooze for Today
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={async () => {
                  const ids = artworkReminders.map(o => o.id);
                  await Promise.all(ids.map(id =>
                    axios.post(`${API}/orders/${id}/snooze-artwork-reminder`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
                  ));
                  toast.success('All snoozed for 7 days');
                  setArtworkReminders([]);
                  setShowArtworkReminderPopup(false);
                }}
                data-testid="snooze-all-artwork-reminders"
              >
                Snooze All 7d
              </Button>
              <Button
                onClick={async () => {
                  const ids = artworkReminders.map(o => o.id);
                  const results = await Promise.all(ids.map(id =>
                    axios.post(`${API}/orders/${id}/send-artwork-reminder`, {}, { headers: { Authorization: `Bearer ${token}` } })
                      .then(r => ({ ok: !!r.data?.success, msg: r.data?.message }))
                      .catch(e => ({ ok: false, msg: e.response?.data?.detail || e.response?.data?.message || 'Failed' }))
                  ));
                  const ok = results.filter(r => r.ok).length;
                  const bad = results.length - ok;
                  if (ok && !bad) toast.success(`Sent ${ok} reminders`);
                  else if (ok && bad) toast.warning(`Sent ${ok}, ${bad} failed — all snoozed for 7 days`);
                  else toast.error('All deliveries failed — snoozed anyway so it won\u2019t nag you.');
                  setArtworkReminders([]);
                  setShowArtworkReminderPopup(false);
                }}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Send All Reminders
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fitting Date Reminders Popup */}
      <Dialog open={showFittingPopup} onOpenChange={setShowFittingPopup}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <DialogTitle className="text-2xl text-green-700">Memorials Ready to Fit!</DialogTitle>
                <DialogDescription>
                  {fittingReminders.ready_to_fit.length} memorial{fittingReminders.ready_to_fit.length !== 1 ? 's are' : ' is'} ready to be fitted
                  {fittingReminders.upcoming_reminders.length > 0 && ` • ${fittingReminders.upcoming_reminders.length} upcoming`}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {/* Ready to Fit - Green Cards */}
            {fittingReminders.ready_to_fit.map(order => (
              <Card key={order.id} className="border-green-300 bg-green-50 cursor-pointer hover:bg-green-100" onClick={() => {
                setShowFittingPopup(false);
                navigate(`/orders/${order.id}`);
              }}>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-green-900">#{order.order_number} - {order.client_name}</h3>
                        <Badge className="bg-green-600">READY TO FIT</Badge>
                      </div>
                      <p className="text-sm text-green-700 mt-1">{order.cemetery}</p>
                      <p className="text-xs text-green-600 mt-1">{order.message}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Upcoming - Amber Cards */}
            {fittingReminders.upcoming_reminders.length > 0 && (
              <>
                <div className="text-sm font-medium text-amber-700 mt-4 pt-4 border-t">
                  ⏳ Coming Up (within 2 weeks)
                </div>
                {fittingReminders.upcoming_reminders.map(order => (
                  <Card key={order.id} className="border-amber-200 bg-amber-50 cursor-pointer hover:bg-amber-100" onClick={() => {
                    setShowFittingPopup(false);
                    navigate(`/orders/${order.id}`);
                  }}>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-amber-900">#{order.order_number} - {order.client_name}</h3>
                          <p className="text-sm text-amber-700">{order.cemetery}</p>
                          <p className="text-xs text-amber-600 mt-1">{order.message}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowFittingPopup(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Dashboard;